/**
 * City hub registry for /case-studies/in/$city pages.
 * Each entry maps a URL slug to a display name, region label, and a list of
 * nearby city slugs. Case studies that primarily belong to a nearby city are
 * shown with a "Serving" badge on the hub page.
 */

export interface CityHub {
  slug: string;
  name: string;
  region: string;
  /** Slugs of neighboring city hubs we still service from this hub. */
  nearby: string[];
}

export const CITY_HUBS: CityHub[] = [
  {
    slug: "san-antonio",
    name: "San Antonio",
    region: "Bexar County",
    nearby: ["new-braunfels", "seguin", "boerne", "san-marcos"],
  },
  {
    slug: "austin",
    name: "Austin",
    region: "Travis County",
    nearby: ["san-marcos", "new-braunfels", "seguin"],
  },
  {
    slug: "new-braunfels",
    name: "New Braunfels",
    region: "Comal County",
    nearby: ["seguin", "san-marcos", "san-antonio"],
  },
  {
    slug: "san-marcos",
    name: "San Marcos",
    region: "Hays County",
    nearby: ["new-braunfels", "seguin", "austin"],
  },
  {
    slug: "seguin",
    name: "Seguin",
    region: "Guadalupe County",
    nearby: ["new-braunfels", "san-antonio", "san-marcos"],
  },
  {
    slug: "boerne",
    name: "Boerne",
    region: "Kendall County",
    nearby: ["san-antonio", "kerrville", "fredericksburg"],
  },
  {
    slug: "kerrville",
    name: "Kerrville",
    region: "Kerr County",
    nearby: ["fredericksburg", "boerne"],
  },
  {
    slug: "fredericksburg",
    name: "Fredericksburg",
    region: "Gillespie County",
    nearby: ["kerrville", "boerne"],
  },
  {
    slug: "houston",
    name: "Houston",
    region: "Harris County",
    nearby: [],
  },
  {
    slug: "corpus-christi",
    name: "Corpus Christi",
    region: "Nueces County / Coastal Bend",
    nearby: ["rockport", "port-aransas"],
  },
  {
    slug: "rockport",
    name: "Rockport",
    region: "Aransas County",
    nearby: ["corpus-christi", "port-aransas"],
  },
  {
    slug: "port-aransas",
    name: "Port Aransas",
    region: "Mustang Island",
    nearby: ["corpus-christi", "rockport"],
  },
];

export function getCityHub(slug: string): CityHub | undefined {
  return CITY_HUBS.find((c) => c.slug === slug);
}

/** Words inside `location` strings that count as a regional match. */
const REGIONAL_KEYWORDS: Record<string, string[]> = {
  "san-antonio": ["South Texas", "Texas"],
  austin: ["Central Texas", "Texas"],
  "new-braunfels": ["Texas Hill Country", "Central Texas", "Texas"],
  "san-marcos": ["Texas Hill Country", "Central Texas", "Texas"],
  seguin: ["Central Texas", "Texas"],
  boerne: ["Texas Hill Country", "Texas"],
  kerrville: ["Texas Hill Country", "Texas"],
  fredericksburg: ["Texas Hill Country", "Texas"],
  houston: ["Gulf Coast", "Texas"],
  "corpus-christi": ["Coastal Bend", "Gulf Coast", "South Texas", "Texas"],
  rockport: ["Coastal Bend", "Gulf Coast", "Texas"],
  "port-aransas": ["Coastal Bend", "Gulf Coast", "Texas"],
};

export function regionalKeywordsFor(slug: string): string[] {
  return REGIONAL_KEYWORDS[slug] ?? ["Texas"];
}
